import React, { useEffect, useState } from "react";
import { Form, Input, Button, Checkbox, notification, Spin, Tabs } from "antd";
import { Row, Col, Carousel } from "react-bootstrap";
import {
  EyeInvisibleOutlined,
  EyeTwoTone,
  MailOutlined,
  MobileOutlined,
  KeyOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { connect } from "react-redux";

import main from "../assets/main.png";
import logo from "../assets/logo.png";
import {
  loginAction,
  sendOtpAction,
  verifyOtpAction,
  setUser,
} from "../store/action/common/authAction";

const RegistrationPage = (props) => {
  const [passwordForm] = Form.useForm();
  const [otpForm] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("password");
  const [identifierValue, setIdentifierValue] = useState("");

  // Notification Helper
  const showNotification = (type, message, description) => {
    notification[type]({
      message: message || "Notification",
      description: description || "",
      placement: "topRight",
    });
  };

  // Helper to check if input looks like a phone number
  const isPhoneLike = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return false;
    return /^[+\d][\d\s-]*$/.test(trimmed);
  };

  // Dynamic icon selector based on input format
  const getIdentifierIcon = () => {
    if (!identifierValue) return <UserOutlined className="text-muted mr-2" />;
    if (isPhoneLike(identifierValue)) {
      return <MobileOutlined className="text-muted mr-2" />;
    }
    return <MailOutlined className="text-muted mr-2" />;
  };

  // Password Login Handler
  const handlePasswordLogin = (values) => {
    props.loginAction_({
      email: (values?.email || "").toLowerCase().trim(),
      password: values?.password,
    });
  };

  // Send OTP Handler
  const handleSendOtp = async () => {
    try {
      const values = await otpForm.validateFields(["identifier"]);
      const rawIdentifier = values?.identifier || identifierValue;
      if (!rawIdentifier) return;

      props.sendOtpAction_({
        identifier: String(rawIdentifier).trim(),
      });
    } catch (err) {
      // Form validation errors handled by Antd
    }
  };

  // Resend OTP Handler
  const handleResendOtp = () => {
    otpForm.setFieldsValue({ otp: "" });
    handleSendOtp();
  };

  // Verify OTP Handler (Guarded against undefined values)
  const handleVerifyOtp = async (values) => {
    // If OTP input is not yet mounted/sent, route "Enter" key press to Send OTP
    if (!props.otpSent) {
      handleSendOtp();
      return;
    }

    const rawIdentifier = values?.identifier || otpForm.getFieldValue("identifier") || identifierValue;
    const rawOtp = values?.otp || otpForm.getFieldValue("otp");

    if (!rawIdentifier || !rawOtp) {
      return;
    }

    props.verifyOtpAction_({
      identifier: String(rawIdentifier).trim(),
      otp: String(rawOtp).trim(),
    });
  };

  // Login Success Handler
  const handleLoginSuccess = (loginData) => {
    const authData = loginData?.data?.accessToken
      ? loginData.data
      : loginData?.data || loginData;
    const userData = authData?.userData;

    if (!authData || !authData.accessToken || !userData) {
      return;
    }

    showNotification(
      "success",
      "Login Successful",
      loginData?.message || "Welcome back!"
    );

    const resolvedUserId =
      userData.userId ?? userData.initialUserId ?? userData._id ?? "";
    const resolvedMongoId = userData._id || "";

    localStorage.setItem("accessToken", authData.accessToken);
    localStorage.setItem("refreshToken", authData.refreshToken || "");
    localStorage.setItem("email", JSON.stringify(userData.email || ""));
    localStorage.setItem("role", userData.role || "");
    localStorage.setItem("name", userData.name || "");
    localStorage.setItem("userId", String(resolvedUserId));
    localStorage.setItem("id", String(resolvedMongoId));
    localStorage.setItem("phone", userData.mobileNo || "");
    localStorage.setItem("companyName", userData.companyName || "");
    localStorage.setItem("firstLogin", String(userData.isFirstLogin ?? false));
    localStorage.setItem("country", userData.country || "");
    localStorage.setItem("domain", userData.categoryName || "");
    localStorage.setItem("domainid", String(userData.category || ""));
    localStorage.setItem(
      "subscriptionSignup",
      userData.subscriptionSignup ? "true" : "false"
    );

    props.setUser_({
      email: userData.email,
      role: userData.role,
      name: userData.name,
    });

    if (userData.role === "business") {
      navigate("/");
    } else if (userData.role === "admin") {
      navigate("/admin");
    } else {
      navigate("/");
    }
  };

  // Prefill Signup Email
  useEffect(() => {
    if (location.state?.signupEmail) {
      const initialEmail = location.state.signupEmail.toLowerCase();
      passwordForm.setFieldsValue({ email: initialEmail });
      otpForm.setFieldsValue({ identifier: initialEmail });
      setIdentifierValue(initialEmail);
    }
  }, [location.state, passwordForm, otpForm]);

  // -------------------------------------------------------------
  // RESPONSE LISTENERS (HANDLES "OTP SENT SUCCESSFULLY" & OTHERS)
  // -------------------------------------------------------------

  // Listener for Send OTP Response (Inspects both otpResponse & loginResponse fallback)
  // useEffect(() => {
  //   const res = props.otpResponse || (props.otpSent ? props.loginResponse : null);
  //   if (!res) return;

  //   const msg = res?.message || "Operation completed";

  //   if (res?.success === true) {
  //     showNotification("success", "OTP Sent", msg);
  //   } else if (res?.success === false) {
  //     showNotification("error", "OTP Request Failed", msg);
  //     otpForm.setFields([
  //       {
  //         name: "identifier",
  //         errors: [msg],
  //       },
  //     ]);
  //   }
  // }, [props.otpResponse, props.loginResponse, props.otpSent, otpForm]);


  useEffect(() => {
  const res = props.otpResponse;
  if (!res) return;

  const msg = res?.message || "Operation completed";

  if (res?.success === true) {
    showNotification("success", "OTP Sent", msg);
  } else if (res?.success === false) {
    showNotification("error", "OTP Request Failed", msg);
    otpForm.setFields([
      {
        name: "identifier",
        errors: [msg],
      },
    ]);
  }
}, [props.otpResponse, otpForm]);
  // Listener for Password Login & OTP Verification Responses
  useEffect(() => {
    if (!props.loginResponse) return;

    const res = props.loginResponse;
    const msg = res?.message || "Request completed";
    const activePayload = res?.data?.accessToken ? res.data : res?.data || res;

    if (activePayload?.accessToken) {
      handleLoginSuccess(res);
      return;
    }

    if (res?.success === false) {
      if (activeTab === "password") {
        showNotification("error", "Login Failed", msg);
        passwordForm.setFields([
          {
            name: "password",
            errors: [msg],
          },
        ]);
      } else if (activeTab === "otp" && props.otpSent) {
        showNotification("error", "OTP Verification Failed", msg);
        otpForm.setFields([
          {
            name: "otp",
            errors: [msg],
          },
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loginResponse, activeTab, props.otpSent, passwordForm, otpForm]);

  // Password Error Catch-All (HTTP 4xx/5xx)
  useEffect(() => {
    if (props.error && activeTab === "password") {
      const errMsg =
        props.error?.response?.data?.message ||
        props.error?.message ||
        (typeof props.error === "string" ? props.error : "Invalid credentials");

      showNotification("error", "Login Failed", errMsg);

      passwordForm.setFields([
        {
          name: "password",
          errors: [errMsg],
        },
      ]);
    }
  }, [props.error, activeTab, passwordForm]);

  // OTP Error Catch-All (HTTP 4xx/5xx)
  useEffect(() => {
    if (props.otpError && activeTab === "otp") {
      const errMsg =
        props.otpError?.response?.data?.message ||
        props.otpError?.message ||
        (typeof props.otpError === "string" ? props.otpError : "Invalid OTP provided");

      showNotification("error", "OTP Error", errMsg);

      otpForm.setFields([
        {
          name: props.otpSent ? "otp" : "identifier",
          errors: [errMsg],
        },
      ]);
    }
  }, [props.otpError, activeTab, props.otpSent, otpForm]);

  // Tab Items Configuration
  const tabItems = [
    {
      key: "password",
      label: "Password Login",
      children: (
        <Form
          form={passwordForm}
          name="passwordLoginForm"
          layout="vertical"
          onFinish={handlePasswordLogin}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: "Please input your email!" },
              { type: "email", message: "Please enter a valid email address!" },
            ]}
          >
            <Input
              prefix={<MailOutlined className="text-muted mr-2" />}
              placeholder="name@company.com"
              size="large"
              style={{ borderRadius: "5px" }}
              onChange={(e) => {
                const lower = e.target.value.toLowerCase();
                passwordForm.setFieldsValue({ email: lower });
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: "Please input your password!" },
              { min: 6, message: "Password must be at least 6 characters!" },
            ]}
          >
            <Input.Password
              prefix={<KeyOutlined className="text-muted mr-2" />}
              placeholder="Enter password"
              size="large"
              iconRender={(visible) =>
                visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
              }
              style={{ borderRadius: "5px" }}
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <Checkbox>Remember me</Checkbox>
            <Link to="/forgot-password" style={{ color: "#0048FF" }}>
              Forgot / Reset password
            </Link>
          </div>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              style={{
                width: "100%",
                borderRadius: "5px",
                backgroundColor: "#0048FF",
                borderColor: "#0048FF",
              }}
              loading={props.loading}
              disabled={props.loading}
            >
              {props.loading ? <Spin /> : "Sign In"}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: "otp",
      label: "OTP Login",
      children: (
        <Form
          form={otpForm}
          name="otpLoginForm"
          layout="vertical"
          onFinish={handleVerifyOtp}
        >
          <Form.Item
            name="identifier"
            label="Email or Phone Number"
            rules={[
              {
                required: true,
                message: "Please enter your email or phone number!",
              },
            ]}
          >
            <Input
              prefix={getIdentifierIcon()}
              placeholder="Enter Email or Mobile Number"
              size="large"
              style={{ borderRadius: "5px" }}
              disabled={false}
              onPressEnter={(e) => {
                if (!props.otpSent) {
                  e.preventDefault();
                  handleSendOtp();
                }
              }}
              onChange={(e) => setIdentifierValue(e.target.value)}
            />
          </Form.Item>

          {props.otpSent && (
            <Form.Item
              name="otp"
              label="Verification Code"
              rules={[
                { required: true, message: "Please enter the OTP!" },
                { len: 6, message: "OTP must be exactly 6 digits!" },
              ]}
            >
              <Input
                placeholder="Enter 6-digit OTP"
                size="large"
                maxLength={6}
                style={{ borderRadius: "5px", letterSpacing: "2px" }}
                onChange={() => {
                  if (otpForm.getFieldError("otp").length > 0) {
                    otpForm.setFields([{ name: "otp", errors: [] }]);
                  }
                }}
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginTop: "24px" }}>
            {!props.otpSent ? (
              <Button
                type="primary"
                size="large"
                onClick={handleSendOtp}
                style={{
                  width: "100%",
                  borderRadius: "5px",
                  backgroundColor: "#0048FF",
                  borderColor: "#0048FF",
                }}
                loading={props.otpLoading}
                disabled={props.otpLoading}
              >
                Send OTP
              </Button>
            ) : (
              <div className="d-flex flex-column gap-2">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  style={{
                    width: "100%",
                    borderRadius: "5px",
                    backgroundColor: "#0048FF",
                    borderColor: "#0048FF",
                  }}
                  loading={props.loading}
                  disabled={props.loading}
                >
                  Verify & Sign In
                </Button>
                <Button
                  type="link"
                  onClick={handleResendOtp}
                  loading={props.otpLoading}
                  disabled={props.otpLoading}
                  style={{ color: "#0048FF", textAlign: "center" }}
                >
                  Resend OTP
                </Button>
              </div>
            )}
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div
      className="container-fluid"
      style={{ height: "100vh", backgroundColor: "#E6EDF3" }}
    >
      <Row className="h-100">
        <Col
          md={6}
          className="d-flex flex-column justify-content-center align-items-center bg-white p-5"
        >
          <div style={{ maxWidth: "400px", width: "100%" }}>
            <img
              src={logo}
              alt="Logo"
              className="mb-4"
              style={{ width: "120px" }}
            />
            <h1 className="mb-1" style={{ fontSize: "1.8rem" }}>
              Welcome Back
            </h1>
            <p className="text-muted mb-4">
              Please enter your details to log in
            </p>

            {location.state?.showPasswordHint && (
              <p style={{ color: "green", fontWeight: "500" }}>
                Password has been sent to your email
              </p>
            )}

            <div
              style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                items={tabItems}
                centered
              />
            </div>

            <p className="mt-4 text-center">
              Don't have an account?{" "}
              <a
                href="https://stageretailindia.blaccdot.ca/sign-up"
                target="_blank"
                rel="noreferrer"
                className="text-primary font-weight-bold"
              >
                Sign up
              </a>
            </p>
          </div>
        </Col>

        <Col
          md={6}
          className="d-none d-md-flex flex-column justify-content-center align-items-center text-white loginCarousel"
          style={{ backgroundColor: "#075AD8", textAlign: "center" }}
        >
          <Carousel
            controls={false}
            indicators={false}
            interval={3000}
            pause={false}
          >
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-1"
                className="d-block"
                style={{ width: "60%", margin: "auto" }}
              />
            </Carousel.Item>
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-2"
                className="d-block"
                style={{ width: "60%", margin: "auto" }}
              />
            </Carousel.Item>
            <Carousel.Item>
              <img
                src={main}
                alt="Illustration-3"
                className="d-block"
                style={{ width: "60%", margin: "auto" }}
              />
            </Carousel.Item>
          </Carousel>

          <h2 style={{ fontSize: "1.5rem", marginTop: "20px" }}>
            Start Converting Leads Fast
          </h2>
          <p
            className="text-center"
            style={{ maxWidth: "400px", fontSize: "1rem", marginTop: "10px" }}
          >
            Unlock exclusive insights and opportunities—log in to grow your
            business!
          </p>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loginResponse: state.login.response,
  loading: state.login.loading,
  error: state.login.error,
  otpSent: state.login.otpSent,
  otpLoading: state.login.otpLoading,
  otpError: state.login.otpError,
  otpResponse: state.login.otpResponse,
});

const mapDispatchToProps = (dispatch) => ({
  loginAction_: (data) => {
    dispatch(loginAction(data));
  },
  sendOtpAction_: (data) => {
    dispatch(sendOtpAction(data));
  },
  verifyOtpAction_: (data) => {
    dispatch(verifyOtpAction(data));
  },
  setUser_: (data) => {
    dispatch(setUser(data));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RegistrationPage);

// import React, { useEffect, useState } from "react";
// import { Form, Input, Button, Checkbox, notification } from "antd";
// import { Row, Container, Col, Carousel } from "react-bootstrap";
// import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
// import main from "../assets/main.png";
// import logo from "../assets/logo.png";
// import { loginAction, setUser } from "../store/action/common/authAction";
// import { useNavigate, useLocation } from "react-router-dom";
// import { connect } from "react-redux";
// import { Spin } from "antd";
// import { Link } from "react-router-dom";

// const RegistrationPage = (props) => {
//   const [form] = Form.useForm();

//   const navigate = useNavigate();
//   const location = useLocation();

//   const onFinish = (values) => {
//     // Dispatch Redux Saga action
//     props.loginAction_(values);
//   };

//   const onFinishFailed = (errorInfo) => {
//     console.log("Failed:", errorInfo);
//   };

//   useEffect(() => {
//     if (location.state?.signupEmail) {
//       form.setFieldsValue({ email: location.state.signupEmail.toLowerCase(), });
//     }
//   }, [location.state, form]);

//   useEffect(() => {
//     if (props.loginResponse && props.loginResponse.accessToken) {
//       localStorage.setItem("accessToken", props.loginResponse.accessToken);
//       localStorage.setItem("refreshToken", props.loginResponse.refreshToken);
//       localStorage.setItem(
//         "email",
//         JSON.stringify(props.loginResponse.userData.email),
//       );
//       localStorage.setItem("role", props.loginResponse.userData.role);
//       localStorage.setItem("name", props.loginResponse.userData.name);
//       localStorage.setItem("userId", props.loginResponse.userData.userId);
//       localStorage.setItem("id", props.loginResponse.userData._id);
//       localStorage.setItem("phone", props.loginResponse.userData.mobileNo);
//       localStorage.setItem(
//         "companyName",
//         props.loginResponse.userData.companyName,
//       );
//       localStorage.setItem(
//         "firstLogin",
//         props.loginResponse.userData.isFirstLogin,
//       );
//       localStorage.setItem("country", props.loginResponse.userData.country);
//       localStorage.setItem("domain", props.loginResponse.userData.categoryName);
//       localStorage.setItem("domainid", props.loginResponse.userData.category);
//       localStorage.setItem(
//         "subscriptionSignup",
//         props.loginResponse.userData.subscriptionSignup ? "true" : "false",
//       );
//       props.setUser_({
//         email: localStorage.getItem("email"),
//         role: localStorage.getItem("role"),
//         name: localStorage.getItem("name"),
//       });
//       if (props.loginResponse.userData.role === "business") {
//         navigate("/");
//       } else if (props.loginResponse.userData.role === "admin") {
//         navigate("/admin");
//       }
//     }
//   }, [props.loginResponse]);

//   useEffect(() => {
//     if (props.error) {
//       form.setFields([
//         {
//           name: "password",
//           errors: [props.error.message || "Invalid credentials"],
//         },
//       ]);

//       //   if (props.error) {  // Remove the status check
//       //     notification.error({
//       //         message: "Login Failed",
//       //         description: props.error.message || "Something went wrong.",
//       //         placement: "topRight",
//       //         duration: 3,
//       //     });
//       // }
//     }
//   }, [props.error]);

//   return (
//     <div
//       className="container-fulid"
//       style={{ height: "100vh", backgroundColor: "#E6EDF3" }}
//     >
//       <Row className="h-100">
//         {/* Left Panel */}
//         <Col
//           md={6}
//           className="d-flex flex-column justify-content-center align-items-center bg-white p-5"
//         >
//           <div style={{ maxWidth: "400px", width: "100%" }}>
//             <img
//               src={logo}
//               alt="Logo"
//               className="mb-4"
//               style={{ width: "120px" }}
//             />
//             <h1 className="mb-3" style={{ fontSize: "1.8rem" }}>
//               Login to your account
//             </h1>
//             <p className="text-muted mb-4">Enter your credentials below</p>
//             {location.state?.showPasswordHint && (
//               <p style={{ color: "green", fontWeight: "500" }}>
//                 Password has been sent to your email
//               </p>
//             )}

//             <Form
//               form={form}
//               name="loginForm"
//               layout="vertical"
//               onFinish={onFinish}
//               onFinishFailed={onFinishFailed}
//               style={{
//                 maxWidth: "400px",
//                 margin: "auto",
//                 background: "#fff",
//                 padding: "20px",
//                 borderRadius: "8px",
//                 boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               <Form.Item
//                 name="email"
//                 label="Email"
//                 rules={[
//                   { required: true, message: "Please input your email!" },
//                   { type: "email", message: "Please enter a valid email!" },
//                 ]}
//               >
//                 <Input
//                   placeholder="Email"
//                   size="large"
//                   style={{ borderRadius: "5px", padding: "10px" }}
//                   onChange={(e) => {
//                     const lower = e.target.value.toLowerCase();
//                     form.setFieldsValue({ email: lower });
//                   }}
//                 />
//               </Form.Item>

//               <Form.Item
//                 name="password"
//                 label="Password"
//                 validateStatus={props.error ? "error" : ""}
//                 help={
//                   props.error
//                     ? props.error.message || "Invalid credentials"
//                     : null
//                 }
//                 rules={[
//                   { required: true, message: "Please input your password!" },
//                   {
//                     min: 6,
//                     message: "Password must be at least 6 characters!",
//                   },
//                 ]}
//               >
//                 <Input.Password
//                   placeholder="Password"
//                   size="large"
//                   iconRender={(visible) =>
//                     visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
//                   }
//                   style={{ borderRadius: "5px", padding: "10px" }}
//                 />
//               </Form.Item>

//               <div
//                 style={{
//                   display: "flex",
//                   justifyContent: "space-between",
//                   alignItems: "center",
//                   marginBottom: "16px",
//                 }}
//               >
//                 <Checkbox>Remember me</Checkbox>
//                 <Link to="/forgot-password" style={{ color: "#0048FF" }}>
//                   Forgot / Reset password
//                 </Link>
//               </div>

//               <Form.Item>
//                 <Button
//                   type="primary"
//                   htmlType="submit"
//                   size="large"
//                   style={{
//                     width: "100%",
//                     borderRadius: "5px",
//                     backgroundColor: "#0048FF",
//                     borderColor: "#0048FF",
//                   }}
//                   loading={props.loading}
//                   disabled={props.loading}
//                 >
//                   {props.loading ? <Spin /> : "Login"}
//                 </Button>
//               </Form.Item>
//             </Form>
//             <p className="mt-3">
//               Don’t have an account?{" "}
//               <a
//                 // href="https://blaccdot.com/"
//                 href="https://stageretailindia.blaccdot.ca/sign-up"
//                 target="_blank"
//                 className="text-primary"
//               >
//                 Sign up
//               </a>
//             </p>
//           </div>
//         </Col>

//         {/* Right Panel */}
//         <Col
//           md={6}
//           className="d-none d-md-flex flex-column justify-content-center align-items-center text-white loginCarousel"
//           style={{ backgroundColor: "#075AD8", textAlign: "center" }}
//         >
//           <Carousel
//             controls={false}
//             indicators={false}
//             interval={3000}
//             pause={false}
//           >
//             {/* Slide 1 */}
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-1"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             {/* Slide 2 */}
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration 2"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             {/* Slide 3 */}
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration 3"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//           </Carousel>

//           <h2 style={{ fontSize: "1.5rem", marginTop: "20px" }}>
//             Start Converting Leads Fast
//           </h2>
//           <p
//             className="text-center"
//             style={{ maxWidth: "400px", fontSize: "1rem", marginTop: "10px" }}
//           >
//             Unlock exclusive insights and opportunities—log in to grow your
//             business!
//           </p>
//         </Col>
//       </Row>
//     </div>
//   );
// };

// const mapStateToProps = (state) => ({
//   loginResponse: state.login.response,
//   loading: state.login.loading, // Map the loading state
//   error: state.login.error,
// });

// const mapDispatchToProps = (dispatch) => ({
//   loginAction_: (data) => {
//     dispatch(loginAction(data));
//   },
//   setUser_: (data) => {
//     dispatch(setUser(data));
//   },
// });

// export default connect(mapStateToProps, mapDispatchToProps)(RegistrationPage);

///////////////
// import React, { useEffect, useState } from "react";
// import { Form, Input, Button, Checkbox, notification, Spin } from "antd";
// import { Row, Col, Carousel } from "react-bootstrap";
// import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
// import { useNavigate, useLocation, Link } from "react-router-dom";
// import { connect } from "react-redux";

// import main from "../assets/main.png";
// import logo from "../assets/logo.png";
// import {
//   loginAction,
//   sendOtpAction,
//   verifyOtpAction,
//   setUser,
// } from "../store/action/common/authAction";

// const RegistrationPage = (props) => {
//   const [form] = Form.useForm();
//   const navigate = useNavigate();
//   const location = useLocation();

//   // State Management
//   const [isPhone, setIsPhone] = useState(false);

//   // Helper to distinguish Email vs. Phone Number
//   const detectInputType = (value = "") => {
//     const cleanValue = value.trim();
//     // Basic pattern checking for digit-only or phone strings
//     const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
//     const isDigitsOnly = /^\d+$/.test(cleanValue.replace(/[\s+()-]/g, ""));

//     if (isDigitsOnly || (phoneRegex.test(cleanValue) && !cleanValue.includes("@"))) {
//       setIsPhone(true);
//     } else {
//       setIsPhone(false);
//     }
//   };

//   const handleIdentifierChange = (e) => {
//     const val = e.target.value;
//     detectInputType(val);
//   };

//   // Dispatch standard password-based login
//   const handlePasswordLogin = (values) => {
//     props.loginAction_(values);
//   };

//   // Dispatch Send OTP Redux Action
//   const handleSendOtp = async () => {
//     try {
//       const identifier = await form.validateFields(["identifier"]);
//       if (!identifier.identifier) return;

//       props.sendOtpAction_({
//         identifier: identifier.identifier.trim(),
//       });
//     } catch (err) {
//       // Form validation handles basic missing input errors
//     }
//   };

//   // Dispatch Verify OTP Redux Action
//   const handleVerifyOtp = async () => {
//     try {
//       const values = await form.validateFields(["identifier", "otp"]);
//       props.verifyOtpAction_({
//         identifier: values.identifier.trim(),
//         otp: values.otp.trim(),
//       });
//     } catch (err) {
//       // Form validation handles missing inputs
//     }
//   };

//   // Common function to save tokens and route user
//   const handleLoginSuccess = (loginData) => {
//     // 1. Unwrap payload: handles root data vs. double-wrapped `data.data`
//     const authData = loginData?.data?.accessToken ? loginData.data : (loginData?.data || loginData);
//     const userData = authData?.userData;

//     if (!authData || !authData.accessToken || !userData) {
//       return;
//     }

//     // 2. Extract key User Identifiers safely with fallbacks
//     const resolvedUserId = userData.userId ?? userData.initialUserId ?? userData._id ?? "";
//     const resolvedMongoId = userData._id || "";

//     // 3. Populate LocalStorage safely
//     localStorage.setItem("accessToken", authData.accessToken);
//     localStorage.setItem("refreshToken", authData.refreshToken || "");
//     localStorage.setItem("email", JSON.stringify(userData.email || ""));
//     localStorage.setItem("role", userData.role || "");
//     localStorage.setItem("name", userData.name || "");
//     localStorage.setItem("userId", String(resolvedUserId));
//     localStorage.setItem("id", String(resolvedMongoId));
//     localStorage.setItem("phone", userData.mobileNo || "");
//     localStorage.setItem("companyName", userData.companyName || "");
//     localStorage.setItem("firstLogin", String(userData.isFirstLogin ?? false));
//     localStorage.setItem("country", userData.country || "");
//     localStorage.setItem("domain", userData.categoryName || "");
//     localStorage.setItem("domainid", String(userData.category || ""));
//     localStorage.setItem(
//       "subscriptionSignup",
//       userData.subscriptionSignup ? "true" : "false"
//     );

//     // 4. Update Redux User Context
//     props.setUser_({
//       email: userData.email,
//       role: userData.role,
//       name: userData.name,
//     });

//     // 5. Navigate based on Role
//     if (userData.role === "business") {
//       navigate("/");
//     } else if (userData.role === "admin") {
//       navigate("/admin");
//     } else {
//       navigate("/");
//     }
//   };

//   const onFinish = (values) => {
//     if (!isPhone) {
//       // Direct pass-through for email & password authentication
//       handlePasswordLogin({
//         email: values.identifier.toLowerCase(),
//         password: values.password,
//       });
//     } else {
//       // OTP Form Submission
//       handleVerifyOtp();
//     }
//   };

//   useEffect(() => {
//     if (location.state?.signupEmail) {
//       const initialEmail = location.state.signupEmail.toLowerCase();
//       form.setFieldsValue({ identifier: initialEmail });
//       detectInputType(initialEmail);
//     }
//   }, [location.state, form]);

//   // Sync state & navigation when login or OTP verification succeeds
//   useEffect(() => {
//     if (!props.loginResponse) return;

//     // Resolve payload structure whether nested inside `data` or flat
//     const activePayload = props.loginResponse?.data?.accessToken
//       ? props.loginResponse.data
//       : (props.loginResponse?.data || props.loginResponse);

//     if (activePayload && activePayload.accessToken) {
//       handleLoginSuccess(props.loginResponse);
//     }
//   }, [props.loginResponse]);

//   // Handle password login errors
//   useEffect(() => {
//     if (props.error && !isPhone) {
//       form.setFields([
//         {
//           name: "password",
//           errors: [props.error.message || "Invalid credentials"],
//         },
//       ]);
//     }
//   }, [props.error, isPhone]);

//   // Handle OTP sending/verifying errors
//   useEffect(() => {
//     if (props.otpError && isPhone) {
//       notification.error({
//         message: "OTP Error",
//         description: props.otpError.message || "Something went wrong.",
//       });

//       form.setFields([
//         {
//           name: props.otpSent ? "otp" : "identifier",
//           errors: [props.otpError.message || "Action failed"],
//         },
//       ]);
//     }
//   }, [props.otpError, isPhone, props.otpSent]);

//   return (
//     <div
//       className="container-fluid"
//       style={{ height: "100vh", backgroundColor: "#E6EDF3" }}
//     >
//       <Row className="h-100">
//         {/* Left Panel */}
//         <Col
//           md={6}
//           className="d-flex flex-column justify-content-center align-items-center bg-white p-5"
//         >
//           <div style={{ maxWidth: "400px", width: "100%" }}>
//             <img
//               src={logo}
//               alt="Logo"
//               className="mb-4"
//               style={{ width: "120px" }}
//             />
//             <h1 className="mb-3" style={{ fontSize: "1.8rem" }}>
//               Login to your account
//             </h1>
//             <p className="text-muted mb-4">Enter your credentials below</p>

//             {location.state?.showPasswordHint && (
//               <p style={{ color: "green", fontWeight: "500" }}>
//                 Password has been sent to your email
//               </p>
//             )}

//             <Form
//               form={form}
//               name="loginForm"
//               layout="vertical"
//               onFinish={onFinish}
//               style={{
//                 maxWidth: "400px",
//                 margin: "auto",
//                 background: "#fff",
//                 padding: "20px",
//                 borderRadius: "8px",
//                 boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
//               }}
//             >
//               {/* Flexible Input (Email or Phone) */}
//               <Form.Item
//                 name="identifier"
//                 label="Email or Phone Number"
//                 rules={[
//                   {
//                     required: true,
//                     message: "Please input your email or phone number!",
//                   },
//                 ]}
//               >
//                 <Input
//                   placeholder="Enter Email or Phone Number"
//                   size="large"
//                   style={{ borderRadius: "5px", padding: "10px" }}
//                   onChange={handleIdentifierChange}
//                 />
//               </Form.Item>

//               {/* Password Field (Rendered if Input is Email) */}
//               {!isPhone && (
//                 <Form.Item
//                   name="password"
//                   label="Password"
//                   validateStatus={props.error ? "error" : ""}
//                   help={
//                     props.error
//                       ? props.error.message || "Invalid credentials"
//                       : null
//                   }
//                   rules={[
//                     { required: true, message: "Please input your password!" },
//                     {
//                       min: 6,
//                       message: "Password must be at least 6 characters!",
//                     },
//                   ]}
//                 >
//                   <Input.Password
//                     placeholder="Password"
//                     size="large"
//                     iconRender={(visible) =>
//                       visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
//                     }
//                     style={{ borderRadius: "5px", padding: "10px" }}
//                   />
//                 </Form.Item>
//               )}

//               {/* OTP Input Field (Rendered if Input is Phone and OTP is Sent) */}
//               {isPhone && props.otpSent && (
//                 <Form.Item
//                   name="otp"
//                   label="Enter OTP"
//                   rules={[
//                     { required: true, message: "Please enter the OTP!" },
//                     { len: 6, message: "OTP must be 6 digits!" },
//                   ]}
//                 >
//                   <Input
//                     placeholder="Enter 6-digit OTP"
//                     size="large"
//                     maxLength={6}
//                     style={{ borderRadius: "5px", padding: "10px" }}
//                   />
//                 </Form.Item>
//               )}

//               {/* Actions & Utilities */}
//               {!isPhone && (
//                 <div
//                   style={{
//                     display: "flex",
//                     justifyContent: "space-between",
//                     alignItems: "center",
//                     marginBottom: "16px",
//                   }}
//                 >
//                   <Checkbox>Remember me</Checkbox>
//                   <Link to="/forgot-password" style={{ color: "#0048FF" }}>
//                     Forgot / Reset password
//                   </Link>
//                 </div>
//               )}

//               {/* Submit Buttons */}
//               <Form.Item>
//                 {!isPhone ? (
//                   // Email Login Button
//                   <Button
//                     type="primary"
//                     htmlType="submit"
//                     size="large"
//                     style={{
//                       width: "100%",
//                       borderRadius: "5px",
//                       backgroundColor: "#0048FF",
//                       borderColor: "#0048FF",
//                     }}
//                     loading={props.loading}
//                     disabled={props.loading}
//                   >
//                     {props.loading ? <Spin /> : "Login"}
//                   </Button>
//                 ) : !props.otpSent ? (
//                   // Request OTP Button
//                   <Button
//                     type="primary"
//                     size="large"
//                     onClick={handleSendOtp}
//                     style={{
//                       width: "100%",
//                       borderRadius: "5px",
//                       backgroundColor: "#0048FF",
//                       borderColor: "#0048FF",
//                     }}
//                     loading={props.otpLoading}
//                     disabled={props.otpLoading}
//                   >
//                     Send OTP
//                   </Button>
//                 ) : (
//                   // Verify OTP Button
//                   <Button
//                     type="primary"
//                     htmlType="submit"
//                     size="large"
//                     style={{
//                       width: "100%",
//                       borderRadius: "5px",
//                       backgroundColor: "#0048FF",
//                       borderColor: "#0048FF",
//                     }}
//                     loading={props.loading}
//                     disabled={props.loading}
//                   >
//                     Verify & Login
//                   </Button>
//                 )}
//               </Form.Item>
//             </Form>

//             <p className="mt-3">
//               Don’t have an account?{" "}
//               <a
//                 href="https://stageretailindia.blaccdot.ca/sign-up"
//                 target="_blank"
//                 rel="noreferrer"
//                 className="text-primary"
//               >
//                 Sign up
//               </a>
//             </p>
//           </div>
//         </Col>

//         {/* Right Panel */}
//         <Col
//           md={6}
//           className="d-none d-md-flex flex-column justify-content-center align-items-center text-white loginCarousel"
//           style={{ backgroundColor: "#075AD8", textAlign: "center" }}
//         >
//           <Carousel
//             controls={false}
//             indicators={false}
//             interval={3000}
//             pause={false}
//           >
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-1"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-2"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//             <Carousel.Item>
//               <img
//                 src={main}
//                 alt="Illustration-3"
//                 className="d-block"
//                 style={{ width: "60%", margin: "auto" }}
//               />
//             </Carousel.Item>
//           </Carousel>

//           <h2 style={{ fontSize: "1.5rem", marginTop: "20px" }}>
//             Start Converting Leads Fast
//           </h2>
//           <p
//             className="text-center"
//             style={{ maxWidth: "400px", fontSize: "1rem", marginTop: "10px" }}
//           >
//             Unlock exclusive insights and opportunities—log in to grow your
//             business!
//           </p>
//         </Col>
//       </Row>
//     </div>
//   );
// };

// const mapStateToProps = (state) => ({
//   loginResponse: state.login.response,
//   loading: state.login.loading,
//   error: state.login.error,
//   // Mapping OTP Redux states
//   otpSent: state.login.otpSent,
//   otpLoading: state.login.otpLoading,
//   otpError: state.login.otpError,
// });

// const mapDispatchToProps = (dispatch) => ({
//   loginAction_: (data) => {
//     dispatch(loginAction(data));
//   },
//   sendOtpAction_: (data) => {
//     dispatch(sendOtpAction(data));
//   },
//   verifyOtpAction_: (data) => {
//     dispatch(verifyOtpAction(data));
//   },
//   setUser_: (data) => {
//     dispatch(setUser(data));
//   },
// });

// export default connect(mapStateToProps, mapDispatchToProps)(RegistrationPage);
